import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import IconInterax from './Icon'
import { INTERAX_COLORS } from '../constants/colors'

export interface DashboardBoxStatusProps {
  title: string
  activeCount: number
  percentChange: number
  changeText: string
  icon: React.ReactNode
  className?: string
}

const DashboardBoxStatus: React.FC<DashboardBoxStatusProps> = ({
  title,
  activeCount,
  percentChange,
  changeText,
  icon,
  className = ''
}) => {
  const { t } = useTranslation()

  const { hasPercentChange, changeColor, ChangeIcon } = useMemo(() => {
    const hasChange = percentChange !== 0
    const positive = percentChange > 0
    const negative = percentChange < 0

    let color: string = INTERAX_COLORS.thunderGrey
    let IconComponent: typeof ArrowTrendingUpIcon | typeof ArrowTrendingDownIcon | null = null

    if (positive) {
      color = INTERAX_COLORS.green
      IconComponent = ArrowTrendingUpIcon
    } else if (negative) {
      color = INTERAX_COLORS.redError
      IconComponent = ArrowTrendingDownIcon
    }

    return {
      hasPercentChange: hasChange,
      changeColor: color,
      ChangeIcon: IconComponent
    }
  }, [percentChange])

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        p: { xs: 2, sm: 2, md: 4 },
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Left side - Content */}
      <Box
        sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-between', width: '100%' }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
            {t(title)}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              color: INTERAX_COLORS.white,
              fontWeight: 600,
              fontSize: { xs: '32px', sm: '36px', md: '40px' },
              lineHeight: 1.1
            }}
          >
            {activeCount}
          </Typography>
        </Box>
        {/* Right side - Icon with circular background */}
        <IconInterax backgroundShape="circle" size="medium">
          {icon}
        </IconInterax>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'flex-start', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasPercentChange ? (
            <>
              {ChangeIcon && <ChangeIcon aria-hidden style={{ width: 16, height: 16, color: changeColor }} />}
              <Typography component="span" variant="body2" sx={{ color: changeColor, fontWeight: 600 }}>
                {Math.abs(percentChange)}%
              </Typography>
              {changeText && (
                <Typography component="span" variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                  {changeText}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
              {changeText}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default DashboardBoxStatus
